import { useEffect, useState } from "react";
import { getCountdown, formatCountdown, formatFullDateTime } from "@/lib/timezone";
import { Clock, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

interface AuctionCountdownProps {
  targetUtc: string;
  label?: string;
  showFullDate?: boolean;
  className?: string;
  onExpire?: () => void;
}

export function AuctionCountdown({
  targetUtc,
  label = "Ends in",
  showFullDate = true,
  className,
  onExpire,
}: AuctionCountdownProps) {
  const [countdown, setCountdown] = useState(() => getCountdown(targetUtc));

  useEffect(() => {
    // Update every second
    const interval = setInterval(() => {
      const newCountdown = getCountdown(targetUtc);
      setCountdown(newCountdown);
      
      if (newCountdown.isExpired && onExpire) {
        onExpire();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetUtc, onExpire]);

  const isUrgent = countdown.totalMs < 60 * 60 * 1000; // Less than 1 hour
  const isExpired = countdown.isExpired;

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="flex items-center gap-2">
        {isUrgent && !isExpired ? (
          <AlertTriangle className="h-4 w-4 text-destructive animate-pulse" />
        ) : (
          <Clock className="h-4 w-4 text-muted-foreground" />
        )}
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <div
        className={cn(
          "text-lg font-semibold",
          isExpired && "text-muted-foreground",
          isUrgent && !isExpired && "text-destructive"
        )}
      >
        {formatCountdown(countdown)}
      </div>
      {showFullDate && (
        <div className="text-xs text-muted-foreground">
          {formatFullDateTime(targetUtc)}
        </div>
      )}
    </div>
  );
}
